import React, { useEffect, useState } from 'react'
import { AppTable, Field } from '../../components/table'
import { Actions, Pager } from '../../components/table/types'
import seeIcon from '../../assets/table_icons/view.svg'
import editIcon from '../../assets/table_icons/edit.svg'
import deleteIcon from '../../assets/table_icons/delete.svg'
import { useTranslation } from 'react-i18next'
import { getTrainingContainer } from '../../container/training-module'
import { ITRainingService, TRAINING_SERVICE_KEY } from '../../modules/training'
import { Query } from '../../common/api/Query'
import { ROUTE_TRAININGS, ROUTE_TRAININGS_CREATE } from '../../routes/routes-constants'
import { navigate } from '@reach/router'
import { TrainingDTO } from '../../modules/training/models/TrainingDTO'
import { getUserContainer } from '../../container/user-module'
import { UserService } from '../../modules/users/services/UserService'
import { LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { User } from '../../modules/users/models/User'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { reduceString } from '../../common/utils/strings'
import { Box, Modal } from '@mui/material'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from '../../common/status/StatusService'
import { CustomModal } from '../../components/modal/CustomModal'
import { Pagination } from '../../components/table/Pagination'
import { AppButton, ButtonTheme } from 'components/app-button/AppButton'
import { PageLayout } from 'pages/layout'

const userContainer = getUserContainer()
const userService = userContainer.get<UserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const trainingService = getTrainingContainer().get<ITRainingService>(TRAINING_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

type TableTrainingsProps = {
  id?: string
}

export function Table(props: TableTrainingsProps): JSX.Element {
  const { t } = useTranslation('translation')
  const loggedUser = loggedUserService.get()
  const [user, setUser] = useState<User>()
  const [page, setPage] = useState<number>(0)
  const [pager, setPager] = useState<Pager>()
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [count, setCount] = useState<number>(0)
  const [trainings, setTrainings] = useState<TrainingDTO[]>([])
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map())
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [currentTraining, setCurrentTraining] = useState<TrainingDTO>()
  const innerWidth = window.innerWidth

  useEffect(() => {
    if (!loggedUser?.id) return
    userService.getByID(loggedUser?.id).subscribe((res) => {
      res && setUser(res)
    })
  }, [loggedUser?.id])

  useEffect(() => {
    if (!isLoading) return
    trainingService
      .getFilteredList(
        new Query({
          pager: { offset: page * rowsPerPage, limit: rowsPerPage },
          sort: [{ field: 'title' }],
        })
      )
      .subscribe((res) => {
        if (!res) return
        setTrainings(
          res.items.map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            image: item.image,
            creatorID: item.creatorID,
          }))
        )
        setCount(res.count)
        setIsLoading(false)
      })
  }, [isLoading, page, rowsPerPage])

  useEffect(() => {
    if (!trainings.length) {
      return
    }

    const creatorIds = trainings
      .map((t) => t.creatorID)
      .filter((id, index, self) => self.indexOf(id) === index)
    if (creatorIds.length === 0) return

    userService
      .getFilteredList(
        new Query({
          pager: { offset: 0, limit: creatorIds.length },
          query: [{ name: 'ids', value: creatorIds }],
        })
      )
      .subscribe((res) =>
        setUserNames(
          new Map(res.items.map((u) => [u.id, `${u.firstName} ${u.lastName}`] as [string, string]))
        )
      )
  }, [trainings])

  useEffect(() => {
    if (count > 0) {
      setPager({
        page,
        count,
        handleChangePage,
        rowsPerPage,
        handleChangeRowsPerPage,
      })
    }
  }, [page, count, rowsPerPage])

  const createTraining = () => navigate(ROUTE_TRAININGS_CREATE)

  const handlerSeeTraining = (training: TrainingDTO) => {
    navigate(`${ROUTE_TRAININGS}/${training.id}`)
  }

  const editTraining = (training: TrainingDTO) => {
    navigate(`/trainings/form/${training.id}`)
  }

  const removeTraining = (training: TrainingDTO) => {
    setCurrentTraining(training)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
  }

  const handleDeleteTraining = () => {
    if (currentTraining?.id) {
      trainingService.delete(currentTraining.id).subscribe((success) => {
        if (success) {
          statusService.sendStatus({ variant: 'success' })
          setIsLoading(true)
        }
      })
    }
    setOpenDeleteModal(false)
  }

  const handleChangePage = (event: unknown, value: number) => {
    setPage(value)
    setIsLoading(true)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number.parseInt(event.target.value) || 10
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    setIsLoading(true)
  }

  const isCreator = (training: TrainingDTO) => training.creatorID === loggedUser?.id

  const fields: Field<TrainingDTO>[] = [
    {
      name: 'title',
      label: t('title'),
      renderFunc: (field, item) => t(item.title),
    },
    {
      name: 'description',
      label: t('description'),
      renderFunc: (field, item) => reduceString(t(item.description), 50),
    },
    {
      name: 'creatorID',
      label: t('creator'),
      renderFunc: (f, i) => userNames.get(i.creatorID) || 'Cargando...',
    },
  ]

  const actions: Actions<TrainingDTO> = {
    actionsColumn: t('actions'),
    items: [
      {
        handler: handlerSeeTraining,
        icon: seeIcon,
        label: 'see',
      },
      {
        handler: editTraining,
        icon: editIcon,
        label: 'edit',
        hidden: (training) => !isCreator(training),
      },
      {
        handler: removeTraining,
        icon: deleteIcon,
        label: 'delete',
        hidden: (training) => !isCreator(training),
      },
    ],
  }

  return (
    <PageLayout>
      <AppTable
        items={trainings}
        rowKeyField="id"
        fields={fields}
        actions={actions}
        pager={pager}
        isLoading={isLoading}
      />

      <Box
        display={'flex'}
        justifyContent={'space-between'}
        width={'100%'}
        alignItems={'center'}
        mt={2}
      >
        <Box sx={{ marginLeft: '10px' }}>{pager && <Pagination {...pager} />}</Box>
        <Box sx={{ marginRight: '30px' }}>
          <AppButton
            label={t('addTraining')}
            theme={ButtonTheme.NewPrimary}
            handler={createTraining}
            type={'button'}
          />
        </Box>
      </Box>

      <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <CustomModal
          title={t('deleteTraining')}
          warningText={t('irreversibleTrainingAction')}
          handleClose={handleCloseDeleteModal}
          handleSave={handleDeleteTraining}
        />
      </Modal>
    </PageLayout>
  )
}
